// HubSidebar — the ClientHub navigation sidebar with live counts (Epic 3 · S3.1).
//
// NOW / WORK / CLIENTS / CONVERS. / EFFORT + ADMIN. Counts are live (fn_now,
// fn_work_list, fn_conversation_rooms, and a v_client_attention count for
// Clients), polled every 60s — not per navigation. A count still loading shows a
// spinner; a count that failed to load hides its badge (null ≠ zero).
// Never reorders a list under someone's cursor — the badge is just a number.
//
// This is a SEPARATE sidebar from the analytics NavSidebar. The analytics sidebar
// is for the park-facing dashboards; this is the ops tool's own navigation.

import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSidebarCounts } from "../../../hooks/useSidebarCounts";
import { useWorkspace } from "../../../contexts/WorkspaceContext";
import { useAuth } from "../../../contexts/AuthContext";
// ── Icons (inline SVG, no new dependency) ────────────────────────────────────
const I = ({ children, ...p }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>{children}</svg>
);
const NowIcon = () => <I><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 15 14"/></I>;
const WorkIcon = () => <I><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></I>;
const ClientsIcon = () => <I><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></I>;
const ConversIcon = () => <I><path d="M21 15a4 4 0 01-4 4H7l-4 4V5a4 4 0 014-4h10a4 4 0 014 4z"/></I>;
const EffortIcon = () => <I><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></I>;
const OrgIcon = () => <I><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/><path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/></I>;
const UserIcon = () => <I><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></I>;
const ClientMgmtIcon = () => <I><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></I>;
const MailboxIcon = () => <I><rect x="3" y="10" width="18" height="10" rx="1"/><path d="M3 10l3-6h12l3 6"/><path d="M7 14h4"/></I>;
const SendIcon = () => <I><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></I>;
const CapabilityIcon = () => <I><path d="M12 2l3 6 6 1-4.5 4 1 6L12 16l-5.5 3 1-6L3 9l6-1 3-6z"/></I>;
const TeamIcon = () => <I><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></I>;
const SignalIcon = () => <I><path d="M4 12a8 8 0 0116 0"/><path d="M7 12a5 5 0 0110 0"/><circle cx="12" cy="12" r="1"/></I>;
const LogoutIcon = () => <I><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></I>;

// A small spinner shown in a badge slot while a count is still being fetched, so
// the row reads "loading in a moment" rather than looking permanently empty.
const CountSpinner = () => (
  <span className="ml-auto shrink-0" role="status" aria-label="Loading count">
    {/* Inherit the row's text colour (currentColor) so the spinner is white on the
        active blue row and gray on the rest — a fixed gray vanished on blue. */}
    <svg className="h-3.5 w-3.5 animate-spin text-current opacity-70" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  </span>
);

// A badge with the live count. While the first fetch is in flight, a null count
// shows a spinner (it IS coming); after that, null → hidden (couldn't load) and
// 0 → shown (nothing needs you). This distinction matters: a hidden badge says
// "we don't know", a zero says "you're clear" — conflating them is a lie.
const CountBadge = ({ count, loading }) => {
  if (count == null) return loading ? <CountSpinner /> : null;
  return (
    <span
      className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
        count > 0
          ? "bg-red-500 text-white"
          : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
      }`}
      aria-label={count > 0 ? `${count} items need you` : "No items"}
    >
      {count}
    </span>
  );
};

const NavItem = ({ to, icon: Icon, label, count, loading, end }) => {
  const location = useLocation();
  const isActive = end ? location.pathname === to : location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      end={end}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? "bg-[#32B1FF] text-white"
          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
      }`}
    >
      <Icon />
      <span>{label}</span>
      {count !== undefined && <CountBadge count={count} loading={loading} />}
    </NavLink>
  );
};

const AdminItem = ({ to, icon: Icon, label }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);
  return (
    <NavLink
      to={to}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? "bg-[#32B1FF] text-white"
          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
      }`}
    >
      <Icon />
      <span>{label}</span>
    </NavLink>
  );
};

export default function HubSidebar({ className = "", nowCount }) {
  const { counts, loading } = useSidebarCounts();
  // The Now page passes the total of the scope it is showing via `nowCount`, so the badge
  // matches the page's own header count while it is open. Elsewhere, and on the Now page while
  // that total is unknown (null), the badge falls back to the polled personal count.
  const nowBadge = nowCount ?? counts.now;
  const { workspaces, can } = useWorkspace();
  const canManageAgents = can("agent.manage");
  const canManageAdmin = can("admin.manage");

  // Logout — mirrors AdminHeader/ClientHeader exactly: AuthContext.signOut()
  // calls supabase.auth.signOut() (drops the live session + Supabase's own
  // sb-*-auth-token keys) then we navigate to "/". A confirmation modal guards
  // against an accidental click, matching the dashboard's behavior so the hub
  // and the dashboard log out identically.
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);
  const handleLogout = async () => {
    setShowLogout(false);
    await signOut();
    navigate("/");
  };

  // Admin section visibility. Organizations, Users, and the admin Clients
  // management page are platform-admin screens; all three are gated by
  // `admin.manage` so staff (who don't hold it — see WorkspaceContext.js) never
  // see the links. The DB is still the wall, but the menu no longer tempts a
  // click that only refuses.
  //
  // Note the two different "Clients" surfaces: the primary nav Clients
  // (/hub/clients, the ops client-attention list, RLS-scoped, served to
  // everyone) vs. the admin Clients here (/clients = ProjectManagementPage,
  // the admin CRUD page). They are distinct pages; staff reach the hub list
  // via the primary nav, not via this admin entry.
  const adminItems = useMemo(
    () => [
      ...(canManageAdmin
        ? [
            { to: "/organizations", icon: OrgIcon, label: "Organizations" },
            { to: "/clients", icon: ClientMgmtIcon, label: "Clients" },
            { to: "/users", icon: UserIcon, label: "Users" },
          ]
        : []),
    ],
    [canManageAdmin],
  );

  // The whole Admin section collapses when nothing in it is visible — i.e. for
  // a staff member who holds neither `admin.manage` nor `agent.manage`.
  const hasAdminItems =
    adminItems.length > 0 || canManageAdmin || canManageAgents;

  return (
    <nav
      className={`flex h-full w-60 flex-col border-r border-gray-200 bg-white px-3 py-4 dark:border-gray-800 dark:bg-gray-900 ${className}`}
      aria-label="ClientHub navigation"
      data-testid="hub-sidebar"
    >
      <div className="mb-4 flex items-center gap-2 px-2">
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
          Insider Perks
        </span>
        <span className="rounded bg-[#32B1FF]/10 px-1.5 py-0.5 text-xs font-semibold text-[#32B1FF]">
          ClientHub
        </span>
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto">
        <NavItem to="/hub/now" icon={NowIcon} label="Now" count={nowBadge} loading={loading} end />
        <NavItem to="/hub/work" icon={WorkIcon} label="Work" count={counts.work} loading={loading} />
        <NavItem to="/hub/clients" icon={ClientsIcon} label="Clients" count={counts.clients} loading={loading} />
        <NavItem to="/hub/conversations" icon={ConversIcon} label="Conversations" count={counts.conversations} loading={loading} />
        <NavItem to="/hub/effort" icon={EffortIcon} label="Effort" />

        <div className="pt-4">
          {hasAdminItems && (
            <>
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Admin
              </p>
              <div className="space-y-1">
                {adminItems.map((item) => (
                  <AdminItem key={item.to} {...item} />
                ))}
                {canManageAdmin && (
                  <AdminItem
                    to="/hub/admin/team"
                    icon={TeamIcon}
                    label="Team"
                  />
                )}
                {canManageAdmin && (
                  <AdminItem
                    to="/hub/admin/signals"
                    icon={SignalIcon}
                    label="Signals"
                  />
                )}
                {canManageAgents && (
                  <AdminItem
                    to="/hub/admin/mailboxes"
                    icon={MailboxIcon}
                    label="Mailboxes"
                  />
                )}
                {canManageAgents && (
                  <AdminItem
                    to="/hub/admin/send"
                    icon={SendIcon}
                    label="Send"
                  />
                )}
                {canManageAgents && (
                  <AdminItem
                    to="/hub/admin/capabilities"
                    icon={CapabilityIcon}
                    label="Capabilities"
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer — logout (mirrors AdminHeader/ClientHeader: supabase signOut +
          navigate to "/"). The workspaces count sits above it when present;
          the logout row is pinned at the bottom of the sidebar. */}
      <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-800">
        {workspaces.length > 1 && (
          <div className="px-3 pb-2 text-xs text-gray-400">
            {workspaces.length} workspaces
          </div>
        )}
        <button
          type="button"
          onClick={() => setShowLogout(true)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          title="Logout"
          aria-label="Logout"
          data-testid="hub-logout-btn"
        >
          <LogoutIcon />
          <span>Logout</span>
        </button>
      </div>

      {/* Logout confirmation modal — same shape as ClientHeader's compact
          modal (fixed overlay + centered card + Cancel / Yes, Logout). */}
      {showLogout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 dark:bg-opacity-60">
          <div className="w-full max-w-xs rounded-xl bg-white p-8 shadow-xl dark:bg-gray-800">
            <h2 className="mb-2 text-lg font-bold text-gray-800 dark:text-gray-100">
              Logout
            </h2>
            <div className="mb-5 text-gray-600 dark:text-gray-300">
              Are you sure you want to logout?
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded bg-gray-100 px-4 py-2 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                onClick={() => setShowLogout(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                onClick={handleLogout}
                data-testid="hub-logout-confirm"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

